import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import Sidebar from '../components/Sidebar';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Shield, CheckCircle2, XCircle, Loader2, RefreshCw, AlertTriangle,
  FileText, Users, Clock, Lock, Eye, BookOpen, Clipboard, Bell,
  TrendingUp, Settings, ChevronRight
} from 'lucide-react';

const STATUS_COLORS = { compliant: 'text-emerald-600 bg-emerald-50', in_progress: 'text-amber-600 bg-amber-50', non_compliant: 'text-red-600 bg-red-50' };
const STATUS_LABELS = { compliant: 'HIPAA Compliant', in_progress: 'In Progress', non_compliant: 'Non-Compliant' };
const SEVERITY_COLORS = { critical: 'bg-red-100 text-red-700', high: 'bg-orange-100 text-orange-700', medium: 'bg-amber-100 text-amber-700', low: 'bg-blue-100 text-blue-700' };

export default function HipaaHub() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('HIPAA');
  const [view, setView] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [checklist, setChecklist] = useState(null);
  const [baas, setBaas] = useState(null);
  const [training, setTraining] = useState(null);
  const [breaches, setBreaches] = useState([]);
  const [phiLog, setPhiLog] = useState([]);
  const [auditTrail, setAuditTrail] = useState([]);
  const [retentionPolicy, setRetentionPolicy] = useState(null);
  const [risks, setRisks] = useState([]);
  // Forms
  const [breachForm, setBreachForm] = useState({ title: '', description: '', severity: 'high', phi_type: '', individuals_affected: 0 });
  const [reportingBreach, setReportingBreach] = useState(false);

  const loadOverview = useCallback(async () => {
    try {
      const [clRes, baaRes] = await Promise.all([
        api.get('/api/hipaa/checklist'),
        api.get('/api/hipaa/baa'),
      ]);
      setChecklist(clRes.data);
      setBaas(baaRes.data);
    } catch (e) { /* handled */ } finally { setLoading(false); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { loadOverview(); }, [loadOverview]);

  const loadTraining = async () => { try { const { data } = await api.get('/api/hipaa/training'); setTraining(data); } catch (e) { /* handled */ } };
  const loadBreaches = async () => { try { const { data } = await api.get('/api/hipaa/breaches'); setBreaches(data.breaches || []); } catch (e) { /* handled */ } };
  const loadPhiLog = async () => { try { const { data } = await api.get('/api/hipaa/phi-access'); setPhiLog(data.logs || []); } catch (e) { /* handled */ } };
  const loadAudit = async () => { try { const { data } = await api.get('/api/hipaa/audit-trail'); setAuditTrail(data.logs || []); } catch (e) { /* handled */ } };
  const loadRetention = async () => { try { const { data } = await api.get('/api/hipaa/retention-policy'); setRetentionPolicy(data.policy); } catch (e) { /* handled */ } };
  const loadRisks = async () => { try { const { data } = await api.get('/api/hipaa/risk-assessments'); setRisks(data.assessments || []); } catch (e) { /* handled */ } };

  const toggleChecklistItem = async (itemId, completed) => {
    try {
      await api.put(`/api/hipaa/checklist/${itemId}`, { completed: !completed });
      loadOverview();
    } catch { toast.error('Update failed'); }
  };

  const updateBaa = async (baaId, status) => {
    try {
      await api.put(`/api/hipaa/baa/${baaId}`, { status, signed_date: status === 'signed' ? new Date().toISOString() : '' });
      toast.success('BAA updated');
      loadOverview();
    } catch { toast.error('Update failed'); }
  };

  const markTrained = async (userId) => {
    try {
      await api.put(`/api/hipaa/training/${userId}`, { completed: true });
      toast.success('Training recorded');
      loadTraining();
    } catch { toast.error('Update failed'); }
  };

  const reportBreach = async () => {
    if (!breachForm.title) { toast.error('Title required'); return; }
    setReportingBreach(true);
    try {
      await api.post('/api/hipaa/breach', breachForm);
      toast.success('Breach reported — 72hr notification clock started. Slack + SMS alerts sent.');
      setBreachForm({ title: '', description: '', severity: 'high', phi_type: '', individuals_affected: 0 });
      loadBreaches();
    } catch { toast.error('Report failed'); } finally { setReportingBreach(false); }
  };

  if (loading) return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar activeTab={activeTab} onTabChange={setActiveTab} />
      <main className="flex-1 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></main>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-gray-50" data-testid="hipaa-hub">
      <Sidebar activeTab={activeTab} onTabChange={setActiveTab} />
      <main className="flex-1 p-6 md:p-8 lg:p-10 overflow-y-auto">
        <div className="space-y-6 animate-fade-in">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900 flex items-center gap-2"><Shield className="w-6 h-6 text-[#D4A843]" /> HIPAA Compliance Hub</h1>
              <p className="text-sm text-gray-500 mt-1">Manage PHI protection, BAAs, training, and breach notifications</p>
            </div>
            {checklist && (
              <div className={`px-4 py-2 rounded-lg font-bold text-sm ${STATUS_COLORS[checklist.status]}`} data-testid="hipaa-status">
                {checklist.score}% — {STATUS_LABELS[checklist.status]}
              </div>
            )}
          </div>

          {/* Tabs */}
          <div className="flex gap-1 border-b border-gray-200 pb-0 overflow-x-auto">
            {[
              { k: 'overview', l: 'Overview', icon: TrendingUp },
              { k: 'checklist', l: 'Checklist', icon: Clipboard },
              { k: 'baa', l: 'BAA Tracking', icon: FileText },
              { k: 'training', l: 'Training', icon: BookOpen },
              { k: 'breaches', l: 'Breach Reports', icon: AlertTriangle },
              { k: 'phi', l: 'PHI Access Log', icon: Eye },
              { k: 'audit', l: 'Audit Trail', icon: Lock },
            ].map(t => (
              <button key={t.k} onClick={() => { setView(t.k); if (t.k === 'training' && !training) loadTraining(); if (t.k === 'breaches') loadBreaches(); if (t.k === 'phi') loadPhiLog(); if (t.k === 'audit') loadAudit(); }} className={`px-4 py-2.5 text-xs font-medium border-b-2 transition-colors whitespace-nowrap flex items-center gap-1.5 ${view === t.k ? 'border-[#D4A843] text-[#D4A843]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-testid={`hipaa-tab-${t.k}`}>
                <t.icon className="w-3.5 h-3.5" />{t.l}
              </button>
            ))}
          </div>

          {/* ═══ OVERVIEW ═══ */}
          {view === 'overview' && checklist && (
            <div className="space-y-5" data-testid="hipaa-overview">
              {/* Score Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className={`rounded-lg p-5 text-center ${checklist.score >= 80 ? 'bg-emerald-50 border border-emerald-200' : checklist.score >= 50 ? 'bg-amber-50 border border-amber-200' : 'bg-red-50 border border-red-200'}`}>
                  <p className={`text-4xl font-bold ${checklist.score >= 80 ? 'text-emerald-600' : checklist.score >= 50 ? 'text-amber-600' : 'text-red-600'}`}>{checklist.score}%</p>
                  <p className="text-[10px] text-gray-500 mt-1">Compliance Score</p>
                  <div className="mt-2 h-2 bg-white/50 rounded-full overflow-hidden"><div className={`h-full rounded-full ${checklist.score >= 80 ? 'bg-emerald-500' : checklist.score >= 50 ? 'bg-amber-500' : 'bg-red-500'}`} style={{ width: `${checklist.score}%` }} /></div>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-5 text-center">
                  <p className="text-4xl font-bold text-[#D4A843]">{checklist.completed_items}/{checklist.total_items}</p>
                  <p className="text-[10px] text-gray-500 mt-1">Checklist Items</p>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-5 text-center">
                  <p className="text-4xl font-bold text-gray-900">{baas?.required_signed || 0}/{baas?.required || 0}</p>
                  <p className="text-[10px] text-gray-500 mt-1">BAAs Signed (Required)</p>
                </div>
                <div className="bg-white border border-gray-200 rounded-lg p-5 text-center">
                  <p className="text-4xl font-bold text-[#D4A843]">{checklist.auto_completed}</p>
                  <p className="text-[10px] text-gray-500 mt-1">Auto-Verified (Platform)</p>
                </div>
              </div>

              {/* Category Summary */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {checklist.categories?.map(cat => (
                  <div key={cat.id} className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="text-xs font-bold text-gray-900">{cat.category}</h3>
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded ${cat.completed === cat.total ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{cat.completed}/{cat.total}</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden"><div className={`h-full rounded-full ${cat.completed === cat.total ? 'bg-emerald-500' : 'bg-amber-500'}`} style={{ width: `${(cat.completed / cat.total) * 100}%` }} /></div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ═══ CHECKLIST ═══ */}
          {view === 'checklist' && checklist && (
            <div className="space-y-4" data-testid="hipaa-checklist">
              {checklist.categories?.map(cat => (
                <div key={cat.id} className="bg-white border border-gray-200 rounded-lg p-5">
                  <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <Shield className="w-3.5 h-3.5 text-[#D4A843]" />{cat.category}
                    <span className={`ml-auto text-[10px] font-bold px-2 py-0.5 rounded ${cat.completed === cat.total ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{cat.completed}/{cat.total}</span>
                  </h3>
                  <div className="space-y-2">{cat.items.map(item => (
                    <div key={item.id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                      <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer flex-1">
                        <input type="checkbox" checked={item.completed} disabled={item.auto_check} onChange={() => !item.auto_check && toggleChecklistItem(item.id, item.completed)} className="rounded" data-testid={`check-${item.id}`} />
                        <span className={item.completed ? 'text-gray-500' : 'text-gray-900'}>{item.text}</span>
                        {item.auto_check && <span className="text-[9px] bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded font-bold">AUTO</span>}
                      </label>
                      {item.completed && item.completed_by && <span className="text-[9px] text-gray-400">{item.completed_by} {item.completed_at ? `• ${new Date(item.completed_at).toLocaleDateString()}` : ''}</span>}
                    </div>
                  ))}</div>
                </div>
              ))}
            </div>
          )}

          {/* ═══ BAA TRACKING ═══ */}
          {view === 'baa' && baas && (
            <div className="space-y-4" data-testid="hipaa-baa">
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500">{baas.signed}/{baas.total} BAAs signed ({baas.required_signed}/{baas.required} required)</p>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full text-xs">
                  <thead><tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left py-2.5 px-4 text-[10px] text-gray-500 uppercase font-bold">Vendor</th>
                    <th className="text-left py-2.5 px-4 text-[10px] text-gray-500 uppercase font-bold">Service</th>
                    <th className="text-center py-2.5 px-4 text-[10px] text-gray-500 uppercase font-bold">Required</th>
                    <th className="text-center py-2.5 px-4 text-[10px] text-gray-500 uppercase font-bold">Status</th>
                    <th className="text-right py-2.5 px-4 text-[10px] text-gray-500 uppercase font-bold">Action</th>
                  </tr></thead>
                  <tbody>{baas.baas.map(b => (
                    <tr key={b.id} className="border-b border-gray-50 hover:bg-gray-50/50" data-testid={`baa-${b.id}`}>
                      <td className="py-2.5 px-4 font-medium text-gray-900">{b.vendor}</td>
                      <td className="py-2.5 px-4 text-gray-600">{b.service}</td>
                      <td className="py-2.5 px-4 text-center">{b.required ? <AlertTriangle className="w-3.5 h-3.5 text-amber-500 mx-auto" /> : <span className="text-gray-300">—</span>}</td>
                      <td className="py-2.5 px-4 text-center">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${b.status === 'signed' ? 'bg-emerald-100 text-emerald-700' : b.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{b.status}</span>
                      </td>
                      <td className="py-2.5 px-4 text-right">
                        {b.status !== 'signed' ? (
                          <Button size="sm" onClick={() => updateBaa(b.id, 'signed')} className="bg-emerald-600 text-white rounded text-[10px] h-6 px-2"><CheckCircle2 className="w-3 h-3 mr-0.5" /> Mark Signed</Button>
                        ) : (
                          <Button size="sm" variant="ghost" onClick={() => updateBaa(b.id, 'pending')} className="text-[10px] h-6 text-gray-400">Revoke</Button>
                        )}
                      </td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            </div>
          )}

          {/* ═══ TRAINING ═══ */}
          {view === 'training' && (
            <div className="space-y-4" data-testid="hipaa-training">
              {training ? (
                <>
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-gray-500">{training.trained}/{training.total} staff trained ({training.compliance_rate}%)</p>
                    <Button onClick={loadTraining} variant="outline" size="sm" className="rounded-lg text-xs"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh</Button>
                  </div>
                  <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                    <table className="w-full text-xs">
                      <thead><tr className="bg-gray-50 border-b border-gray-200">
                        <th className="text-left py-2.5 px-4 text-[10px] text-gray-500 uppercase font-bold">Name</th>
                        <th className="text-left py-2.5 px-4 text-[10px] text-gray-500 uppercase font-bold">Role</th>
                        <th className="text-center py-2.5 px-4 text-[10px] text-gray-500 uppercase font-bold">Trained</th>
                        <th className="text-center py-2.5 px-4 text-[10px] text-gray-500 uppercase font-bold">Certificate</th>
                        <th className="text-right py-2.5 px-4 text-[10px] text-gray-500 uppercase font-bold">Action</th>
                      </tr></thead>
                      <tbody>{training.staff.map(s => (
                        <tr key={s.user_id} className="border-b border-gray-50">
                          <td className="py-2.5 px-4 font-medium text-gray-900">{s.name}</td>
                          <td className="py-2.5 px-4 text-gray-600 capitalize">{s.role.replace('_', ' ')}</td>
                          <td className="py-2.5 px-4 text-center">{s.completed ? <CheckCircle2 className="w-4 h-4 text-emerald-500 mx-auto" /> : <XCircle className="w-4 h-4 text-red-400 mx-auto" />}</td>
                          <td className="py-2.5 px-4 text-center font-mono text-[10px] text-gray-500">{s.certificate_id || '—'}</td>
                          <td className="py-2.5 px-4 text-right">
                            {!s.completed && <Button size="sm" onClick={() => markTrained(s.user_id)} className="bg-[#D4A843] text-[#0A0A0A] font-semibold rounded text-[10px] h-6 px-2">Mark Trained</Button>}
                          </td>
                        </tr>
                      ))}</tbody>
                    </table>
                  </div>
                </>
              ) : <div className="flex items-center justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>}
            </div>
          )}

          {/* ═══ BREACH REPORTS ═══ */}
          {view === 'breaches' && (
            <div className="space-y-4" data-testid="hipaa-breaches">
              {/* Report Form */}
              <div className="bg-white border-2 border-red-200 rounded-lg p-5">
                <h3 className="text-xs font-bold text-red-700 uppercase tracking-wider mb-3 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Report a Breach</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                  <div><label className="text-xs text-gray-600">Title *</label><Input value={breachForm.title} onChange={e => setBreachForm({...breachForm, title: e.target.value})} className="mt-1 rounded-lg" placeholder="Brief breach description" data-testid="breach-title" /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><label className="text-xs text-gray-600">Severity</label>
                      <select value={breachForm.severity} onChange={e => setBreachForm({...breachForm, severity: e.target.value})} className="mt-1 h-9 w-full rounded-lg border border-gray-200 px-3 text-xs bg-white" data-testid="breach-severity"><option value="critical">Critical</option><option value="high">High</option><option value="medium">Medium</option><option value="low">Low</option></select></div>
                    <div><label className="text-xs text-gray-600">Individuals Affected</label><Input value={breachForm.individuals_affected} onChange={e => setBreachForm({...breachForm, individuals_affected: Number(e.target.value)})} className="mt-1 rounded-lg" type="number" /></div>
                  </div>
                </div>
                <div className="mb-3"><label className="text-xs text-gray-600">Description</label><textarea value={breachForm.description} onChange={e => setBreachForm({...breachForm, description: e.target.value})} className="mt-1 w-full h-20 rounded-lg border border-gray-200 px-3 py-2 text-sm" placeholder="What happened, what data was exposed..." data-testid="breach-desc" /></div>
                <Button onClick={reportBreach} disabled={reportingBreach} className="bg-red-600 hover:bg-red-700 text-white rounded-lg text-xs" data-testid="report-breach-btn">
                  {reportingBreach ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <AlertTriangle className="w-3.5 h-3.5 mr-1" />} Report Breach (starts 72hr clock)
                </Button>
              </div>

              {/* Breach List */}
              {breaches.map(b => (
                <div key={b.id} className="bg-white border border-gray-200 rounded-lg p-4" data-testid={`breach-${b.id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${SEVERITY_COLORS[b.severity]}`}>{b.severity}</span>
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${b.status === 'resolved' ? 'bg-emerald-100 text-emerald-700' : b.status === 'investigating' ? 'bg-blue-100 text-blue-700' : 'bg-red-100 text-red-700'}`}>{b.status}</span>
                      </div>
                      <h4 className="text-sm font-medium text-gray-900">{b.title}</h4>
                      <p className="text-[10px] text-gray-500 mt-0.5">{b.description?.slice(0, 120)}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] text-red-600 font-bold">Deadline: {new Date(b.notification_deadline).toLocaleString()}</p>
                      <p className="text-[10px] text-gray-400">{b.individuals_affected} affected | By: {b.reported_by_name}</p>
                    </div>
                  </div>
                </div>
              ))}
              {breaches.length === 0 && <p className="text-center py-8 text-xs text-gray-400 bg-white rounded-lg border border-gray-200">No breach reports. Good.</p>}
            </div>
          )}

          {/* ═══ PHI ACCESS LOG ═══ */}
          {view === 'phi' && (
            <div className="space-y-4" data-testid="hipaa-phi">
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500">{phiLog.length} PHI access events</p>
                <Button onClick={loadPhiLog} variant="outline" size="sm" className="rounded-lg text-xs"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh</Button>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full text-xs">
                  <thead><tr className="bg-gray-50 border-b border-gray-200">
                    <th className="text-left py-2.5 px-4 text-[10px] text-gray-500 uppercase font-bold">User</th>
                    <th className="text-left py-2.5 px-4 text-[10px] text-gray-500 uppercase font-bold">Action</th>
                    <th className="text-left py-2.5 px-4 text-[10px] text-gray-500 uppercase font-bold">Resource</th>
                    <th className="text-left py-2.5 px-4 text-[10px] text-gray-500 uppercase font-bold">IP</th>
                    <th className="text-right py-2.5 px-4 text-[10px] text-gray-500 uppercase font-bold">Time</th>
                  </tr></thead>
                  <tbody>{phiLog.map(l => (
                    <tr key={l.id} className="border-b border-gray-50">
                      <td className="py-2.5 px-4 font-medium text-gray-900">{l.user_name} <span className="text-gray-400">({l.user_role})</span></td>
                      <td className="py-2.5 px-4"><span className="px-1.5 py-0.5 bg-gray-100 rounded text-[10px]">{l.action}</span></td>
                      <td className="py-2.5 px-4 text-gray-600">{l.resource_type} {l.resource_id?.slice(0, 8)}</td>
                      <td className="py-2.5 px-4 font-mono text-gray-400">{l.ip_address}</td>
                      <td className="py-2.5 px-4 text-right text-gray-400">{new Date(l.timestamp).toLocaleString()}</td>
                    </tr>
                  ))}</tbody>
                </table>
                {phiLog.length === 0 && <p className="text-center py-8 text-[10px] text-gray-400">No PHI access logged yet</p>}
              </div>
            </div>
          )}

          {/* ═══ AUDIT TRAIL ═══ */}
          {view === 'audit' && (
            <div className="space-y-4" data-testid="hipaa-audit">
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500">HIPAA audit trail</p>
                <Button onClick={loadAudit} variant="outline" size="sm" className="rounded-lg text-xs"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh</Button>
              </div>
              <div className="bg-white border border-gray-200 rounded-lg p-4">
                {auditTrail.length > 0 ? auditTrail.map(l => (
                  <div key={l.id} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0">
                    <div className="flex items-center gap-2">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${l.action?.includes('breach') ? 'bg-red-100 text-red-700' : l.action?.includes('baa') ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'}`}>{l.action?.replace(/_/g, ' ')}</span>
                      <span className="text-xs text-gray-700">{l.user_name}</span>
                    </div>
                    <span className="text-[9px] text-gray-400">{new Date(l.timestamp).toLocaleString()}</span>
                  </div>
                )) : <p className="text-center py-8 text-[10px] text-gray-400">No audit events yet</p>}
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
